import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Client,
  CreateClient,
  ImportClientRow,
  ImportClientsResult,
  UpdateClient,
} from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

export const CLIENTS_PAGE = 50;

export interface ClientPage {
  data: Client[];
  pagination: { limit: number; offset: number; total: number };
}

export function useClients(q: string, offset = 0) {
  return useQuery({
    queryKey: qk.clients(q, offset),
    queryFn: async (): Promise<ClientPage> =>
      unwrap(
        await api.GET('/v1/clients', {
          params: { query: { q: q || undefined, limit: CLIENTS_PAGE, offset } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useClient(id: string | null) {
  return useQuery({
    queryKey: id ? qk.client(id) : ['client', 'none'],
    enabled: Boolean(id),
    queryFn: async (): Promise<Client> =>
      unwrap(await api.GET('/v1/clients/{id}', { params: { path: { id: id! } } })),
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateClient): Promise<Client> =>
      unwrap(await api.POST('/v1/clients', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

/**
 * Bulk CSV import (WS-14). The CSV is parsed CLIENT-SIDE (lib/csvParse) and POSTed as a JSON array —
 * there is no multipart upload. The server FORCES marketing_consent false for every imported client
 * (GDPR), derives the book scope from the session, and returns HONEST counts the modal surfaces
 * verbatim. We invalidate ['clients'] so the freshly-imported rows appear in the list.
 */
export function useImportClients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: ImportClientRow[]): Promise<ImportClientsResult> =>
      unwrap(await api.POST('/v1/clients/import', { body: { clients: rows } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: UpdateClient }): Promise<Client> =>
      unwrap(
        await api.PATCH('/v1/clients/{id}', {
          params: { path: { id: input.id } },
          body: input.body,
        }),
      ),
    onSuccess: (client) => {
      qc.setQueryData(qk.client(client.id), client);
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/clients/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}
